import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ACTIVITY_PRIORITIES, type ActivityType } from "@torre/work-hours";
import Modal from "../../../components/base/Modal";
import { useTheme } from "../../../context/ThemeContext";
import { buildThemeClasses } from "../theme";
import { activityErrorMessage, createActivity, type ActivityRow } from "../services/activitiesService";

// ---------------------------------------------------------------------------
// Alta de una épica (milestone propio) desde el Roadmap. Es un ticket con
// `isEpic: true`: aparece en el Roadmap aunque todavía no tenga hijos, y los
// tickets del backlog se le cuelgan con «Asignar a épica» / «Asignar tickets».
// ---------------------------------------------------------------------------

interface Props {
  catalog: readonly ActivityType[];
  onClose: () => void;
  onSaved: (epic: ActivityRow) => void;
}

export default function EpicFormModal({ catalog, onClose, onSaved }: Props) {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const tc = buildThemeClasses(isDarkMode);
  const types = useMemo(() => catalog.filter((ty) => ty.active !== false), [catalog]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [typeKey, setTypeKey] = useState(() => (types.some((ty) => ty.key === "planning") ? "planning" : types[0]?.key ?? ""));
  const [startDate, setStartDate] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [priority, setPriority] = useState<(typeof ACTIVITY_PRIORITIES)[number]>("medium");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const field = `w-full rounded-lg border px-3 py-2 text-sm outline-none ${tc.border} ${tc.cardBg} ${tc.text}`;
  const label = `mb-1 block text-xs font-medium ${tc.textSecondary}`;

  const submit = async () => {
    setError(null);
    if (title.trim().length < 3) return setError(t("staffActivities.epic.errors.title"));
    if (!typeKey) return setError(t("staffActivities.epic.errors.type"));
    if (startDate && dueDate && startDate > dueDate) return setError(t("staffActivities.epic.errors.dates"));
    setSaving(true);
    try {
      const res = await createActivity({
        title: title.trim(),
        description: description.trim() || null,
        typeKey,
        isEpic: true,
        startDate: startDate || null,
        dueDate: dueDate || null,
        priority,
      });
      onSaved(res.data);
    } catch (err) {
      setSaving(false);
      setError(activityErrorMessage(err) ?? t("staffActivities.common.error"));
    }
  };

  return (
    <Modal
      title={t("staffActivities.epic.title")}
      themeClasses={tc}
      onClose={() => !saving && onClose()}
      width="max-w-xl"
      content={
        <div className="space-y-4" data-testid="epic-form">
          <p className={`text-xs ${tc.textSecondary}`}>{t("staffActivities.epic.hint")}</p>
          <label className={label}>
            {t("staffActivities.epic.name")}
            <input value={title} onChange={(e) => setTitle(e.target.value)} className={field} aria-label={t("staffActivities.epic.name")} data-testid="epic-title" />
          </label>
          <label className={label}>
            {t("staffActivities.epic.description")}
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className={field} aria-label={t("staffActivities.epic.description")} />
          </label>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <label className={label}>
              {t("staffActivities.epic.type")}
              <select value={typeKey} onChange={(e) => setTypeKey(e.target.value)} className={field} aria-label={t("staffActivities.epic.type")}>
                {types.map((ty) => (
                  <option key={ty.key} value={ty.key}>
                    {ty.name}
                  </option>
                ))}
              </select>
            </label>
            <label className={label}>
              {t("staffActivities.epic.start")}
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={field} aria-label={t("staffActivities.epic.start")} />
            </label>
            <label className={label}>
              {t("staffActivities.epic.due")}
              <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className={field} aria-label={t("staffActivities.epic.due")} />
            </label>
          </div>
          <label className={label}>
            {t("staffActivities.epic.priority")}
            <select value={priority} onChange={(e) => setPriority(e.target.value as (typeof ACTIVITY_PRIORITIES)[number])} className={field} aria-label={t("staffActivities.epic.priority")}>
              {ACTIVITY_PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {t(`staffActivities.priority.${p}`, { defaultValue: p })}
                </option>
              ))}
            </select>
          </label>
          {error && (
            <p className="text-xs text-red-500" data-testid="epic-error">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} disabled={saving} className={`rounded-lg border px-3 py-2 text-xs ${tc.border} ${tc.textSecondary}`}>
              {t("staffActivities.common.cancel")}
            </button>
            <button type="button" onClick={() => void submit()} disabled={saving} className="rounded-lg bg-violet-600 px-3 py-2 text-xs font-medium text-white disabled:opacity-60" data-testid="epic-save">
              {saving ? t("staffActivities.epic.creating") : t("staffActivities.epic.create")}
            </button>
          </div>
        </div>
      }
    />
  );
}
